import { useEffect, useRef, useState } from "react";
import Editor from "@monaco-editor/react";
import {
  Code2,
  Wand2,
  Play,
  Loader2,
  AlertTriangle,
  Copy,
  Check,
  FileCode,
  Layout as LayoutIcon,
  Smartphone,
  ExternalLink,
  RotateCw,
  Columns,
  Monitor,
  PanelRightOpen,
  PanelRightClose,
  Terminal,
  Download,
  QrCode,
  ImageIcon,
  ChevronDown,
} from "lucide-react";
import PageHeader from "../components/PageHeader.jsx";
import Markdown from "../components/Markdown.jsx";
import TaskBanner from "../components/TaskBanner.jsx";
import GenerationProgress from "../components/GenerationProgress.jsx";
import ProjectSandpack from "../components/ProjectSandpack.jsx";
import { codeStream, getModels, warmApi } from "../lib/api.js";
import { PROJECT_TEMPLATES } from "../lib/projectTemplates.js";
import { buildSnackUrl, createSavedSnack, EXPO_SDK_VERSION } from "../lib/expoSnack.js";
import QRCode from "react-qr-code";
import QrErrorBoundary from "../components/QrErrorBoundary.jsx";
import DeployGuide from "../components/DeployGuide.jsx";
import {
  buildExpoProjectFiles,
  buildReactWebProjectFiles,
  buildSingleFileExport,
  defaultBundleIds,
  downloadFilesAsZip,
  slugifyIdentifier,
} from "../lib/projectExport.js";
import { usePendingTask, useHomeTaskDone } from "../hooks/usePendingTask.js";
import { resolvePromptText } from "../lib/promptText.js";
import { useI18n } from "../lib/i18n.jsx";
import SpeechToTextButton, {
  appendTranscript,
} from "../components/SpeechToTextButton.jsx";

const LANGUAGES = [
  { id: "javascript", label: "JavaScript" },
  { id: "typescript", label: "TypeScript" },
  { id: "python", label: "Python" },
  { id: "html", label: "HTML" },
  { id: "css", label: "CSS" },
  { id: "json", label: "JSON" },
  { id: "go", label: "Go" },
  { id: "rust", label: "Rust" },
  { id: "java", label: "Java" },
  { id: "cpp", label: "C++" },
  { id: "csharp", label: "C#" },
  { id: "sql", label: "SQL" },
  { id: "shell", label: "Shell" },
  { id: "markdown", label: "Markdown" },
];

const PROJECT_MODES = [
  { id: "single", label: "Single File", icon: FileCode },
  { id: "react-web", label: "React", sublabel: "Website", icon: LayoutIcon },
  { id: "react-native", label: "React Native", sublabel: "Mobile App", icon: Smartphone },
];

const VIEW_MODES = [
  { id: "code", label: "Code", icon: FileCode },
  { id: "split", label: "Split", icon: Columns },
  { id: "preview", label: "Preview", icon: Monitor },
];

const EXPO_GO_VIEW = { id: "expo-go", label: "Expo Go", icon: QrCode };

function viewModesForProject(projectMode) {
  if (projectMode === "react-native") {
    return [...VIEW_MODES, EXPO_GO_VIEW];
  }
  return VIEW_MODES;
}

const SINGLE_STARTERS = {
  javascript: `// Welcome to The Consensus A.I. code editor.
// Ask the AI to write or refactor code on the right.

function greet(name) {
  return \`Hello, \${name}!\`;
}

console.log(greet("Consensus"));
`,
  python: `# Welcome to The Consensus A.I. code editor.
# Ask the AI on the right to write or refactor code.

def greet(name: str) -> str:
    return f"Hello, {name}!"

print(greet("Consensus"))
`,
};

// Pull the first fenced code block out of the AI response.
function extractCode(markdown, language) {
  if (!markdown) return null;

  const langs = [
    language,
    "jsx",
    "javascript",
    "js",
    "tsx",
    "typescript",
    "react",
    "python",
    "html",
    "css",
  ].filter(Boolean);

  const seen = new Set();
  for (const lang of langs) {
    const key = lang.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    const re = new RegExp("```" + lang + "\\s*\\r?\\n([\\s\\S]*?)```", "i");
    const m = markdown.match(re);
    if (m) return m[1].trim();
  }

  const any = markdown.match(/```[\w-]*\s*\r?\n([\s\S]*?)```/);
  if (any) return any[1].trim();

  const trimmed = markdown.trim();
  if (
    /^(import |export |function |const |let |class |\/\/|\/\*|<!DOCTYPE|<html)/i.test(
      trimmed
    )
  ) {
    return trimmed;
  }

  return null;
}

const SUGGESTIONS = {
  single: [
    "Refactor this into smaller, well-named functions.",
    "Add JSDoc comments and basic input validation.",
    "Convert to TypeScript with strict types.",
    "Write unit tests using the Vitest framework.",
  ],
  "react-web": [
    "Build a todo list with add/complete/delete using useState.",
    "Make a glassmorphism login form with two inputs and a button.",
    "Add a dark/light mode toggle that persists in localStorage.",
    "Turn this into a stopwatch with start, stop, and reset.",
  ],
  "react-native": [
    "Build a tap-counter with a big circular button and animated scale.",
    "Make a 3-screen tab layout using a top tab bar and conditional rendering.",
    "Add a styled login screen with TextInput fields and a sign-in Pressable.",
    "Build a FlatList of cards with image, title, and description.",
  ],
};

const WORKSPACE_HEIGHT = {
  single: 640,
  project: 920,
};

export default function CodeEditor() {
  const { t } = useI18n();
  const [models, setModels] = useState([]);
  const [model, setModel] = useState("nvidia/nemotron-3.5-lightning-30b-a3b");
  const [projectMode, setProjectMode] = useState("single");
  const [viewMode, setViewMode] = useState("split"); // sandpack only
  const [showConsole, setShowConsole] = useState(false);
  const [aiOpen, setAiOpen] = useState(true);

  // Single-file state
  const [language, setLanguage] = useState("javascript");
  const [code, setCode] = useState(SINGLE_STARTERS.javascript);
  const [runOutput, setRunOutput] = useState(null);

  // Shared assistant state
  const [prompt, setPrompt] = useState("");
  const [mode, setMode] = useState("edit"); // edit | generate | explain
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState(null);
  const [response, setResponse] = useState("");
  const [copied, setCopied] = useState(false);

  // Project-mode (Sandpack) — remount preview when files change.
  const [projectFiles, setProjectFiles] = useState(null);
  const [previewEpoch, setPreviewEpoch] = useState(0);
  const sandpackBridge = useRef(null);
  const bundlesLinkedRef = useRef(true);
  const autoAskRef = useRef(false);

  const [appConfig, setAppConfig] = useState(() => ({
    appName: "Consensus App",
    iconDataUrl: null,
    ...defaultBundleIds("Consensus App"),
  }));
  const iconInputRef = useRef(null);
  const [expoGoUrl, setExpoGoUrl] = useState("");
  const [snackWebUrl, setSnackWebUrl] = useState("");
  const [expoGoLoading, setExpoGoLoading] = useState(false);
  const [expoGoError, setExpoGoError] = useState(null);
  const [taskBanner, setTaskBanner] = useState(null);

  const cloneTemplateFiles = (mode) => {
    const tpl = PROJECT_TEMPLATES[mode];
    if (!tpl?.files) return null;
    return Object.fromEntries(
      Object.entries(tpl.files).map(([path, file]) => [path, { ...file }])
    );
  };

  useEffect(() => {
    warmApi();
    getModels()
      .then((m) => setModels(m.code || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (projectMode === "single") {
      setProjectFiles(null);
      setPreviewEpoch(0);
    }
  }, [projectMode]);

  useEffect(() => {
    if (projectMode !== "single") return;
    if (SINGLE_STARTERS[language]) setCode(SINGLE_STARTERS[language]);
    else setCode(`// ${language} starter\n`);
    setRunOutput(null);
  }, [language, projectMode]);

  const selectProjectMode = (mode) => {
    setProjectMode(mode);
    setError(null);
    setExpoGoUrl("");
    setSnackWebUrl("");
    setExpoGoError(null);
    if (mode !== "react-native" && viewMode === "expo-go") {
      setViewMode("split");
    }
    if (mode === "single") {
      setProjectFiles(null);
      setPreviewEpoch(0);
      return;
    }
    const appName = mode === "react-native" ? "Consensus App" : "Consensus Web App";
    setAppConfig({ appName, iconDataUrl: null, ...defaultBundleIds(appName) });
    bundlesLinkedRef.current = true;
    setProjectFiles(cloneTemplateFiles(mode));
    setPreviewEpoch((e) => e + 1);
  };

  const template = PROJECT_TEMPLATES[projectMode];

  const ask = async (promptOverride) => {
    const activePrompt = resolvePromptText(promptOverride, prompt);
    if (!activePrompt || loading) return;
    setPrompt(activePrompt);
    setLoading(true);
    setError(null);
    setResponse("");
    setStatus(t("common.starting", "Starting…"));

    // Build context for the AI based on the active project mode.
    const ctx = (() => {
      if (projectMode === "single") {
        return { language, code };
      }
      const lang = "jsx";
      const currentApp =
        sandpackBridge.current?.getPrimary?.() ??
        projectFiles?.[template.primaryFile]?.code ??
        template?.files?.[template.primaryFile]?.code ??
        "";
      return { language: lang, code: currentApp };
    })();

    try {
      const text = await codeStream(
        {
          model,
          prompt: activePrompt,
          code: ctx.code,
          language: ctx.language,
          mode,
          projectMode,
        },
        {
          onStatus: setStatus,
          onDelta: (_delta, full) => setResponse(full),
        }
      );

      if (mode !== "explain") {
        const extracted = extractCode(text, ctx.language);
        if (extracted) {
          if (projectMode === "single") {
            setCode(extracted);
          } else {
            const base = projectFiles ?? cloneTemplateFiles(projectMode) ?? {};
            const nextFiles = {
              ...base,
              [template.primaryFile]: {
                ...(base[template.primaryFile] || {}),
                code: extracted,
              },
            };
            setProjectFiles(nextFiles);
            // Remount Sandpack so preview picks up the new App.js.
            setPreviewEpoch((e) => e + 1);
          }
        } else {
          setError(
            "AI replied but no code block was found. Try asking for a single fenced code block."
          );
        }
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      setStatus("");
    }
  };

  const copyCode = async () => {
    const text =
      projectMode === "single"
        ? code
        : sandpackBridge.current?.getPrimary?.() ?? "";
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  // Lightweight in-browser runner for JS only (used in Single File mode).
  const run = () => {
    setRunOutput(null);
    if (language !== "javascript" && language !== "typescript") {
      setRunOutput({
        ok: false,
        lines: [`In-browser run only supports JavaScript. (${language} skipped.)`],
      });
      return;
    }
    const lines = [];
    const log = (...args) =>
      lines.push(
        args
          .map((a) => {
            if (typeof a === "string") return a;
            try {
              return JSON.stringify(a, null, 2);
            } catch {
              return String(a);
            }
          })
          .join(" ")
      );
    try {
      // eslint-disable-next-line no-new-func
      const fn = new Function("console", code);
      fn({ log, info: log, warn: log, error: log });
      setRunOutput({ ok: true, lines: lines.length ? lines : ["(no output)"] });
    } catch (err) {
      setRunOutput({ ok: false, lines: [String(err)] });
    }
  };

  const updateAppName = (appName) => {
    setAppConfig((prev) => {
      const next = { ...prev, appName };
      if (bundlesLinkedRef.current && projectMode === "react-native") {
        Object.assign(next, defaultBundleIds(appName));
      }
      return next;
    });
  };

  const updateIosBundleId = (iosBundleId) => {
    bundlesLinkedRef.current = false;
    setAppConfig((prev) => ({ ...prev, iosBundleId }));
  };

  const updateAndroidPackage = (androidPackage) => {
    bundlesLinkedRef.current = false;
    setAppConfig((prev) => ({ ...prev, androidPackage }));
  };

  const updateAppIcon = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("App icon must be a PNG or JPEG image.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setAppConfig((prev) => ({ ...prev, iconDataUrl: reader.result }));
    };
    reader.onerror = () => setError("Could not read the app icon file.");
    reader.readAsDataURL(file);
  };

  const clearAppIcon = () => {
    setAppConfig((prev) => ({ ...prev, iconDataUrl: null }));
    if (iconInputRef.current) iconInputRef.current.value = "";
  };

  const getPrimaryCode = () =>
    sandpackBridge.current?.getPrimary?.() ??
    projectFiles?.[template?.primaryFile]?.code ??
    template?.files?.[template?.primaryFile]?.code ??
    "";

  const refreshExpoGoQr = async () => {
    if (projectMode !== "react-native") return;
    const code = getPrimaryCode();
    if (!code.trim()) {
      setExpoGoError("Add some code before generating an Expo Go link.");
      setExpoGoUrl("");
      return;
    }
    setExpoGoLoading(true);
    setExpoGoError(null);
    try {
      const saved = await createSavedSnack(code, { name: appConfig.appName });
      setExpoGoUrl(saved.expoGoUrl);
      setSnackWebUrl(saved.snackWebUrl);
    } catch (e) {
      setExpoGoUrl("");
      setSnackWebUrl("");
      setExpoGoError(e.message || "Could not create Expo Go link.");
    } finally {
      setExpoGoLoading(false);
    }
  };

  useEffect(() => {
    if (projectMode !== "react-native") {
      setExpoGoUrl("");
      setSnackWebUrl("");
      setExpoGoError(null);
      return;
    }
    const timer = setTimeout(() => {
      refreshExpoGoQr();
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectMode, projectFiles, previewEpoch, appConfig.appName]);

  usePendingTask("/code", (task) => {
    setTaskBanner(task.rawPrompt || task.prompt);
    const { codeMode, mode: aiMode, language: lang } = task.params || {};
    if (codeMode) selectProjectMode(codeMode);
    if (lang && codeMode === "single") setLanguage(lang);
    if (aiMode) setMode(aiMode);
    setPrompt(task.prompt);
    autoAskRef.current = true;
  });

  const taskDone = useHomeTaskDone(loading, error, Boolean(taskBanner));

  useEffect(() => {
    if (!autoAskRef.current || loading || !prompt.trim()) return;
    if (projectMode !== "single" && !projectFiles) return;
    autoAskRef.current = false;
    const timer = setTimeout(() => {
      ask(prompt);
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectMode, projectFiles, prompt, loading]);

  const getSandpackSnapshot = () => {
    const live = sandpackBridge.current?.getAllFiles?.();
    if (live && Object.keys(live).length) return live;
    const stateFiles = projectFiles ?? template?.files;
    if (!stateFiles) return {};
    return Object.fromEntries(
      Object.entries(stateFiles).map(([path, file]) => [
        path,
        typeof file === "string" ? file : file?.code ?? "",
      ])
    );
  };

  const downloadProject = async () => {
    try {
      let files;
      let zipName = slugifyIdentifier(appConfig.appName) || "project";

      if (projectMode === "single") {
        files = buildSingleFileExport({
          code,
          language,
          appName: appConfig.appName,
        });
        zipName = slugifyIdentifier(appConfig.appName || "code");
      } else if (projectMode === "react-native") {
        files = buildExpoProjectFiles({
          ...appConfig,
          sandpackFiles: getSandpackSnapshot(),
        });
      } else {
        files = buildReactWebProjectFiles({
          appName: appConfig.appName,
          sandpackFiles: getSandpackSnapshot(),
        });
      }

      await downloadFilesAsZip(files, `${zipName}.zip`);
    } catch (e) {
      setError(e.message || "Download failed.");
    }
  };

  const openInSnack = () => {
    if (snackWebUrl) {
      window.open(snackWebUrl, "_blank", "noopener");
      return;
    }
    const current = getPrimaryCode();
    window.open(
      buildSnackUrl(current, { name: appConfig.appName }),
      "_blank",
      "noopener"
    );
  };

  const resetProject = () => {
    setProjectFiles(cloneTemplateFiles(projectMode));
    setPreviewEpoch((e) => e + 1);
    setResponse("");
  };

  const headerRight = (
    <select
      value={model}
      onChange={(e) => setModel(e.target.value)}
      className="input !w-auto max-w-[280px]"
    >
      {models.length === 0 && <option value={model}>{model}</option>}
      {models.map((m) => (
        <option key={m.id} value={m.id}>
          {m.label}
        </option>
      ))}
    </select>
  );

  const workspaceHeight =
    projectMode === "single" ? WORKSPACE_HEIGHT.single : WORKSPACE_HEIGHT.project;
  const sandpackViewMode = viewMode === "expo-go" ? "code" : viewMode;
  const toolbarViewModes = viewModesForProject(projectMode);

  return (
    <div className="flex flex-col pb-8">
      <PageHeader
        compact
        icon={Code2}
        title={t("nav.code", "AI Code Editor")}
        subtitle={t(
          "code.subtitle",
          "Single-file scratchpad, React websites, or React Native mobile apps."
        )}
        right={headerRight}
      />

      <TaskBanner
        label={t("nav.code", "Code Editor")}
        prompt={taskBanner}
        running={loading}
        error={error}
        completed={taskDone}
      />

      <GenerationProgress
        active={loading}
        message={status}
        className="mx-4 md:mx-10 mt-3"
      />

      {/* Project mode picker */}
      <div className="px-4 md:px-10 py-1.5 border-b border-white/5 bg-ink-900/40 flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 rounded-lg bg-ink-800/80 border border-white/10 p-1">
          {PROJECT_MODES.map((m) => (
            <button
              key={m.id}
              onClick={() => selectProjectMode(m.id)}
              className={[
                "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition",
                projectMode === m.id
                  ? "bg-consensus-500 text-white shadow-glow"
                  : "text-white/60 hover:text-white",
              ].join(" ")}
            >
              <m.icon className="h-3.5 w-3.5 shrink-0" />
              <span className="flex flex-col items-start text-left leading-tight">
                <span>{m.label}</span>
                {m.sublabel && (
                  <span
                    className={[
                      "text-[9px] font-normal tracking-wide",
                      projectMode === m.id ? "text-white/75" : "text-white/40",
                    ].join(" ")}
                  >
                    {m.sublabel}
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>

        {projectMode !== "single" && (
          <div className="flex items-center gap-1 rounded-lg bg-ink-800/80 border border-white/10 p-1">
            {toolbarViewModes.map((m) => (
              <button
                key={m.id}
                onClick={() => setViewMode(m.id)}
                title={
                  m.id === "expo-go"
                    ? t("code.tryExpoGo", "Try on your device with Expo Go")
                    : `${m.label} ${t("common.view", "view")}`
                }
                className={[
                  "flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition",
                  viewMode === m.id
                    ? "bg-white/10 text-white"
                    : "text-white/50 hover:text-white",
                ].join(" ")}
              >
                <m.icon className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">
                  {m.id === "expo-go"
                    ? t("code.viewExpoGo", "Expo Go")
                    : m.label}
                </span>
              </button>
            ))}
          </div>
        )}

        <div className="flex-1" />

        {projectMode !== "single" && (
          <>
            <button
              onClick={() => setShowConsole((v) => !v)}
              className={[
                "btn-ghost !py-1.5 text-xs",
                showConsole && "!text-consensus-300",
              ]
                .filter(Boolean)
                .join(" ")}
              title={t("code.toggleConsole", "Toggle console")}
            >
              <Terminal className="h-3.5 w-3.5" />
              <span className="hidden md:inline">{t("code.console", "Console")}</span>
            </button>
            <button onClick={resetProject} className="btn-ghost !py-1.5 text-xs">
              <RotateCw className="h-3.5 w-3.5" />
              <span className="hidden md:inline">{t("common.reset", "Reset")}</span>
            </button>
            {projectMode === "react-native" && (
              <button onClick={openInSnack} className="btn-secondary !py-1.5 text-xs">
                <ExternalLink className="h-3.5 w-3.5" />
                <span className="hidden md:inline">
                  {t("code.openSnack", "Open in Expo Snack")}
                </span>
              </button>
            )}
            <button onClick={downloadProject} className="btn-secondary !py-1.5 text-xs">
              <Download className="h-3.5 w-3.5" />
              <span className="hidden md:inline">{t("common.download", "Download")}</span>
            </button>
          </>
        )}

        {projectMode === "single" && (
          <button onClick={downloadProject} className="btn-secondary !py-1.5 text-xs">
            <Download className="h-3.5 w-3.5" />
            <span className="hidden md:inline">{t("common.download", "Download")}</span>
          </button>
        )}

        <button
          onClick={() => setAiOpen((v) => !v)}
          className="btn-ghost !py-1.5 text-xs"
          title={
            aiOpen
              ? t("code.hideAiAssistant", "Hide AI assistant")
              : t("code.showAiAssistant", "Show AI assistant")
          }
        >
          {aiOpen ? (
            <PanelRightClose className="h-3.5 w-3.5" />
          ) : (
            <PanelRightOpen className="h-3.5 w-3.5" />
          )}
          <span className="hidden md:inline">
            {aiOpen ? t("code.hideAi", "Hide AI") : t("code.showAi", "Show AI")}
          </span>
        </button>
      </div>

      {(projectMode === "react-web" || projectMode === "react-native") && (
        <details className="group shrink-0 border-b border-white/5 bg-ink-900/30">
          <summary className="cursor-pointer list-none px-4 md:px-10 py-2.5 flex items-center gap-2 text-sm font-medium text-white/85 select-none">
            <ChevronDown className="h-4 w-4 text-white/40 transition group-open:rotate-180" />
            {t("code.appSettings", "App settings")}
            <span className="text-[11px] font-normal text-white/40 ml-1">
              — {appConfig.appName}
            </span>
          </summary>
          <div className="px-4 md:px-10 pb-3 flex flex-wrap items-end gap-x-4 gap-y-2">
          <label className="flex flex-col gap-1 min-w-[160px] flex-1 max-w-xs">
                    <span className="text-[10px] uppercase tracking-widest text-white/40">
                      {t("code.appName", "App name")}
                    </span>
            <input
              type="text"
              value={appConfig.appName}
              onChange={(e) => updateAppName(e.target.value)}
              placeholder="My App"
              className="input !py-1.5 text-xs"
            />
            <SpeechToTextButton
              className="mt-1"
              onTranscript={(text) =>
                updateAppName(appendTranscript(appConfig.appName, text))
              }
            />
          </label>
          {projectMode === "react-native" && (
            <>
              <label className="flex flex-col gap-1 min-w-[200px] flex-1 max-w-sm">
                    <span className="text-[10px] uppercase tracking-widest text-white/40">
                      {t("code.iosBundle", "iOS bundle ID")}
                    </span>
                <input
                  type="text"
                  value={appConfig.iosBundleId}
                  onChange={(e) => updateIosBundleId(e.target.value)}
                  placeholder="com.company.myapp"
                  className="input !py-1.5 text-xs font-mono"
                />
                <SpeechToTextButton
                  className="mt-1"
                  onTranscript={(text) =>
                    updateIosBundleId(appendTranscript(appConfig.iosBundleId, text))
                  }
                />
              </label>
              <label className="flex flex-col gap-1 min-w-[200px] flex-1 max-w-sm">
                    <span className="text-[10px] uppercase tracking-widest text-white/40">
                      {t("code.androidPackage", "Android package")}
                    </span>
                <input
                  type="text"
                  value={appConfig.androidPackage}
                  onChange={(e) => updateAndroidPackage(e.target.value)}
                  placeholder="com.company.myapp"
                  className="input !py-1.5 text-xs font-mono"
                />
                <SpeechToTextButton
                  className="mt-1"
                  onTranscript={(text) =>
                    updateAndroidPackage(
                      appendTranscript(appConfig.androidPackage, text)
                    )
                  }
                />
              </label>
              <div className="flex flex-col gap-1 min-w-[200px]">
                    <span className="text-[10px] uppercase tracking-widest text-white/40">
                      {t("code.appIcon", "App icon")}
                    </span>
                <div className="flex items-center gap-2">
                  {appConfig.iconDataUrl ? (
                    <img
                      src={appConfig.iconDataUrl}
                      alt=""
                      className="h-9 w-9 rounded-lg border border-white/10 object-cover shrink-0"
                    />
                  ) : (
                    <div className="h-9 w-9 rounded-lg border border-dashed border-white/15 bg-white/[0.03] flex items-center justify-center shrink-0">
                      <ImageIcon className="h-4 w-4 text-white/30" />
                    </div>
                  )}
                  <input
                    ref={iconInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(e) => updateAppIcon(e.target.files?.[0])}
                    className="text-[11px] text-white/60 file:mr-2 file:rounded file:border-0 file:bg-white/10 file:px-2 file:py-1 file:text-[11px] file:text-white/80 hover:file:bg-white/15"
                  />
                  {appConfig.iconDataUrl && (
                    <button
                      type="button"
                      onClick={clearAppIcon}
                      className="btn-ghost !py-1 !px-2 text-[11px]"
                    >
                      {t("common.clear", "Clear")}
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
          <p className="text-[11px] text-white/35 pb-1.5 w-full sm:w-auto sm:flex-1 sm:min-w-[200px]">
            {projectMode === "react-native"
              ? "Bundle IDs and icon are written to app.json and assets/ in your download. Expo SDK 54."
              : "Included in package.json and index.html when you download the project."}
          </p>
          </div>
        </details>
      )}

      {projectMode === "single" && (
        <div className="px-4 md:px-10 py-2 border-b border-white/5 bg-ink-900/30 flex items-end gap-3 shrink-0">
          <label className="flex flex-col gap-1 min-w-[160px] max-w-xs flex-1">
            <span className="text-[10px] uppercase tracking-widest text-white/40">
              File name
            </span>
            <input
              type="text"
              value={appConfig.appName}
              onChange={(e) => updateAppName(e.target.value)}
              placeholder="my-script"
              className="input !py-1.5 text-xs"
            />
            <SpeechToTextButton
              className="mt-1"
              onTranscript={(text) =>
                updateAppName(appendTranscript(appConfig.appName, text))
              }
            />
          </label>
          <p className="text-[11px] text-white/35 pb-1.5 hidden sm:block">
            Used as the filename when you download your code as a zip.
          </p>
        </div>
      )}

      <div
        className={[
          "grid",
          aiOpen ? "lg:grid-cols-[1fr,360px]" : "lg:grid-cols-[1fr]",
        ].join(" ")}
        style={{ minHeight: workspaceHeight }}
      >
        {/* Editor / preview panel */}
        <div className="flex flex-col border-r border-white/5">
          {projectMode === "single" ? (
            <>
              <div className="flex items-center gap-2 px-4 py-2 border-b border-white/5 bg-ink-900/60">
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="input !w-auto !py-1.5 text-xs"
                >
                  {LANGUAGES.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.label}
                    </option>
                  ))}
                </select>
                <div className="flex-1" />
                <button onClick={copyCode} className="btn-ghost !py-1.5 text-xs">
                  {copied ? (
                    <>
                      <Check className="h-3.5 w-3.5" /> {t("home.share.copied", "Copied")}
                    </>
                  ) : (
                    <>
                      <Copy className="h-3.5 w-3.5" /> {t("common.copy", "Copy")}
                    </>
                  )}
                </button>
                <button onClick={run} className="btn-secondary !py-1.5 text-xs">
                  <Play className="h-3.5 w-3.5" />
                  {t("common.run", "Run")}
                </button>
              </div>
              <div style={{ height: workspaceHeight - (runOutput ? 192 : 0) }}>
                <Editor
                  height="100%"
                  theme="vs-dark"
                  language={language}
                  value={code}
                  onChange={(v) => setCode(v ?? "")}
                  options={{
                    fontFamily:
                      "JetBrains Mono, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                    fontLigatures: true,
                    fontSize: 13,
                    minimap: { enabled: false },
                    smoothScrolling: true,
                    cursorSmoothCaretAnimation: "on",
                    scrollBeyondLastLine: false,
                    wordWrap: "on",
                    tabSize: 2,
                    padding: { top: 14, bottom: 14 },
                  }}
                />
              </div>
              {runOutput && (
                <div
                  className={[
                    "border-t border-white/5 p-3 max-h-48 overflow-auto text-xs font-mono whitespace-pre-wrap",
                    runOutput.ok
                      ? "bg-black/30 text-white/80"
                      : "bg-red-500/10 text-red-200",
                  ].join(" ")}
                >
                  <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">
                    Output
                  </div>
                  {runOutput.lines.join("\n")}
                </div>
              )}
            </>
          ) : template ? (
            viewMode === "expo-go" ? (
              <ExpoGoPanel
                height={workspaceHeight}
                expoGoUrl={expoGoUrl}
                expoGoLoading={expoGoLoading}
                expoGoError={expoGoError}
                onRefresh={refreshExpoGoQr}
                onOpenSnack={openInSnack}
                t={t}
              />
            ) : (
            <div style={{ height: workspaceHeight }}>
              <ProjectSandpack
                key={`${projectMode}-${previewEpoch}`}
                template={template.template}
                files={projectFiles ?? template.files}
                customSetup={template.customSetup}
                primaryFile={template.primaryFile}
                bridgeRef={sandpackBridge}
                viewMode={sandpackViewMode}
                showConsole={showConsole}
                deviceFrame={projectMode === "react-native" ? "mobile" : false}
              />
            </div>
            )
          ) : null}
        </div>

        {/* AI assistant panel */}
        {aiOpen && (
        <aside
          className="flex flex-col bg-ink-900/40 border-l border-white/5 lg:sticky lg:top-0 lg:self-start lg:max-h-screen"
          style={{ minHeight: workspaceHeight }}
        >
          <div className="px-4 py-3 border-b border-white/5">
            <div className="flex items-center gap-1 rounded-lg bg-ink-800/80 border border-white/10 p-1 text-xs">
                {[
                  { id: "edit", label: t("common.edit", "Edit") },
                  { id: "generate", label: t("common.generate", "Generate") },
                  { id: "explain", label: t("common.explain", "Explain") },
                ].map((tab) => (
                <button
                    key={tab.id}
                    onClick={() => setMode(tab.id)}
                  className={[
                    "flex-1 rounded-md px-2 py-1.5 font-medium transition",
                      mode === tab.id
                      ? "bg-consensus-500 text-white"
                      : "text-white/60 hover:text-white",
                  ].join(" ")}
                >
                    {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3">
            {response ? (
              <div className="card !p-4">
                <Markdown>{response}</Markdown>
              </div>
            ) : (
              <div className="text-sm text-white/50 leading-relaxed">
                <p>
                  Ask the AI to{" "}
                  <span className="text-white/80 font-medium">
                    {mode === "edit"
                      ? projectMode === "single"
                        ? "modify the code on the left"
                        : "modify the current App component"
                      : mode === "generate"
                      ? projectMode === "single"
                        ? "write new code from scratch"
                        : projectMode === "react-web"
                        ? "build a new React web app"
                        : "build a new React Native app"
                      : "explain what the code does"}
                  </span>
                  .{" "}
                  {mode !== "explain" && (
                    <>
                      The first code block in the reply will be applied
                      automatically.
                    </>
                  )}
                </p>
                <ul className="mt-3 space-y-2">
                  {(SUGGESTIONS[projectMode] || SUGGESTIONS.single).map((s) => (
                    <Suggest key={s} setPrompt={setPrompt}>
                      {s}
                    </Suggest>
                  ))}
                </ul>
              </div>
            )}
            {error && (
              <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-2.5 flex gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
          </div>

          <div className="p-3 border-t border-white/5">
            <div className="relative">
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    ask();
                  }
                }}
                placeholder={
                  mode === "edit"
                    ? "How should I change the code?"
                    : mode === "generate"
                    ? projectMode === "react-web"
                      ? "What React app should I build?"
                      : projectMode === "react-native"
                      ? "What React Native app should I build?"
                      : "What should I build?"
                    : "What do you want explained?"
                }
                rows={3}
                className="textarea pr-24 pl-20 text-sm"
              />
              <div className="absolute left-2 bottom-2">
                <SpeechToTextButton
                  onTranscript={(text) =>
                    setPrompt((prev) => appendTranscript(prev, text))
                  }
                  onError={(msg) => setError(msg)}
                />
              </div>
              <button
                onClick={() => ask()}
                disabled={loading || !prompt.trim()}
                className="btn-primary absolute right-2 bottom-2 !py-1.5 !px-3 text-xs"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    {t("common.thinking", "Thinking")}
                  </>
                ) : (
                  <>
                    <Wand2 className="h-3.5 w-3.5" />
                    {t("code.askAi", "Ask AI")}
                  </>
                )}
              </button>
            </div>
            <div className="text-[10px] text-white/30 mt-1.5 px-1">
              {t("code.tipSubmit", "Tip: Cmd/Ctrl + Enter to submit")}
            </div>
          </div>
        </aside>
        )}
      </div>

      {(projectMode === "react-web" || projectMode === "react-native") && (
        <DeployGuide mode={projectMode} />
      )}
    </div>
  );
}

function ExpoGoPanel({
  height,
  expoGoUrl,
  expoGoLoading,
  expoGoError,
  onRefresh,
  onOpenSnack,
  t,
}) {
  return (
    <div
      className="flex flex-col items-center justify-center gap-6 px-6 py-8 bg-ink-900/20 border border-white/5 rounded-none"
      style={{ minHeight: height }}
    >
      <div className="text-center max-w-md">
        <div className="flex items-center justify-center gap-2 text-base font-semibold text-white/90">
          <QrCode className="h-5 w-5 text-consensus-400" />
          {t("code.tryExpoGo", "Try on your device with Expo Go")}
        </div>
        <p className="text-sm text-white/50 mt-2 leading-relaxed">
          Scan with Expo Go (SDK {EXPO_SDK_VERSION}). Tap Refresh QR after editing your code
          so the saved Snack matches your latest App.js.
        </p>
      </div>

      <div className="bg-white p-4 rounded-2xl shadow-lg shrink-0 min-h-[200px] min-w-[200px] flex items-center justify-center">
        {expoGoLoading ? (
          <Loader2 className="h-10 w-10 animate-spin text-ink-900/40" />
        ) : expoGoUrl ? (
          <QrErrorBoundary>
            <QRCode value={expoGoUrl} size={180} level="M" />
          </QrErrorBoundary>
        ) : (
          <div className="text-sm text-ink-900/45 text-center px-4 leading-relaxed">
            QR will appear here
          </div>
        )}
      </div>

      {expoGoError && (
        <p className="text-sm text-red-300 text-center max-w-md">{expoGoError}</p>
      )}

      <div className="flex flex-wrap gap-2 justify-center">
        <button onClick={onRefresh} disabled={expoGoLoading} className="btn-ghost !py-2 text-sm">
          {expoGoLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RotateCw className="h-4 w-4" />
          )}
          {t("code.refreshQr", "Refresh QR")}
        </button>
        <button onClick={onOpenSnack} className="btn-secondary !py-2 text-sm">
          <ExternalLink className="h-4 w-4" />
          {t("code.openSnackBrowser", "Open Snack in browser")}
        </button>
      </div>
    </div>
  );
}

function Suggest({ children, setPrompt }) {
  return (
    <li>
      <button
        onClick={() => setPrompt(children)}
        className="text-left w-full text-[13px] text-white/70 hover:text-white border border-white/10 hover:border-white/20 bg-white/[0.02] hover:bg-white/[0.05] rounded-lg px-3 py-2 transition"
      >
        {children}
      </button>
    </li>
  );
}
